import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "manifest-section"
};
const _hoisted_2 = { class: "d-flex align-center justify-space-between mb-3" };
const _hoisted_3 = { class: "text-body-2 text-medium-emphasis" };
const _hoisted_4 = {
    key: 1,
    class: "text-center py-8 text-medium-emphasis"
};
const _hoisted_5 = {
    key: 2,
    class: "d-flex justify-center mt-4"
};
import { ref, computed, reactive, watch } from "vue";
import { filesize as filesizeLib } from "filesize";
import { useStore } from "@/store";
import { useNotificationStore } from "@/stores/notifications";
import { dserverApi } from "@/services/dserverApi";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DatasetManifest',
    setup(__props) {
        const store = useStore();
        const notifications = useNotificationStore();
        const fetch_identifier = ref(null);
        const downloadingItems = reactive({});
        // Pagination state
        const currentPage = ref(1);
        const itemsPerPage = ref(20);
        const perPageOptions = [10, 20, 50, 100];
        const manifest = computed(() => {
            return store.current_dataset_manifest;
        });
        const numItems = computed(() => {
            return manifest.value && manifest.value.items
                ? Object.values(manifest.value.items).length
                : 0;
        });
        // Get all items as array of [id, item] tuples for easier pagination
        const allItems = computed(() => {
            if (!manifest.value || !manifest.value.items)
                return [];
            return Object.entries(manifest.value.items);
        });
        // Calculate pagination
        const totalPages = computed(() => {
            return Math.ceil(numItems.value / itemsPerPage.value);
        });
        const startIndex = computed(() => {
            return (currentPage.value - 1) * itemsPerPage.value;
        });
        const endIndex = computed(() => {
            return Math.min(startIndex.value + itemsPerPage.value, numItems.value);
        });
        const displayStart = computed(() => {
            return numItems.value > 0 ? startIndex.value + 1 : 0;
        });
        const displayEnd = computed(() => {
            return endIndex.value;
        });
        const paginatedItems = computed(() => {
            return allItems.value.slice(startIndex.value, endIndex.value);
        });
        // Reset page when dataset changes
        watch(() => store.current_dataset?.uri, () => {
            currentPage.value = 1;
        });
        // Reset page when items per page changes
        watch(itemsPerPage, () => {
            currentPage.value = 1;
        });
        const fetch_command = computed(() => {
            if (!store.current_dataset || !fetch_identifier.value)
                return "";
            return ("dtool item fetch " +
                store.current_dataset.uri +
                " " +
                fetch_identifier.value);
        });
        function filesize(bytes) {
            return filesizeLib(bytes);
        }
        function update_fetch_identifier(identifier) {
            fetch_identifier.value = identifier;
        }
        function getFileIcon(filename) {
            const ext = filename.split(".").pop()?.toLowerCase() || "";
            const iconMap = {
                // Images
                png: "mdi-file-image",
                jpg: "mdi-file-image",
                jpeg: "mdi-file-image",
                gif: "mdi-file-image",
                svg: "mdi-file-image",
                webp: "mdi-file-image",
                // Documents
                pdf: "mdi-file-pdf-box",
                doc: "mdi-file-word",
                docx: "mdi-file-word",
                xls: "mdi-file-excel",
                xlsx: "mdi-file-excel",
                ppt: "mdi-file-powerpoint",
                pptx: "mdi-file-powerpoint",
                // Code
                py: "mdi-language-python",
                js: "mdi-language-javascript",
                ts: "mdi-language-typescript",
                html: "mdi-language-html5",
                css: "mdi-language-css3",
                json: "mdi-code-json",
                xml: "mdi-file-xml-box",
                yaml: "mdi-file-code",
                yml: "mdi-file-code",
                // Data
                csv: "mdi-file-delimited",
                txt: "mdi-file-document",
                md: "mdi-language-markdown",
                // Archives
                zip: "mdi-folder-zip",
                tar: "mdi-folder-zip",
                gz: "mdi-folder-zip",
                rar: "mdi-folder-zip",
                "7z": "mdi-folder-zip",
                // Scientific
                hdf5: "mdi-database",
                h5: "mdi-database",
                nc: "mdi-database",
                npy: "mdi-database",
                npz: "mdi-database",
            };
            return iconMap[ext] || "mdi-file";
        }
        async function downloadItem(identifier, relpath) {
            const uri = store.current_dataset?.uri;
            if (!uri)
                return;
            downloadingItems[identifier] = true;
            try {
                // Get signed URL for the item
                const response = await dserverApi.getItemSignedUrl(uri, identifier);
                const signedUrl = response.url;
                // Fetch the item content from the signed URL
                const itemResponse = await fetch(signedUrl);
                if (!itemResponse.ok) {
                    throw new Error(`Failed to download item: ${itemResponse.statusText}`);
                }
                // Get the blob and create download link
                const blob = await itemResponse.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                // Extract filename from relpath
                const filename = relpath.split("/").pop() || relpath;
                // Create and trigger download
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                // Clean up the URL
                window.URL.revokeObjectURL(downloadUrl);
            }
            catch (err) {
                console.error("Failed to download item:", err);
                const message = err instanceof Error ? err.message : String(err);
                notifications.error(`Download failed: ${message}`);
            }
            finally {
                downloadingItems[identifier] = false;
            }
        }
        async function copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
                notifications.success("Copied to clipboard");
            }
            catch (err) {
                console.error("Failed to copy:", err);
                notifications.error("Failed to copy to clipboard");
            }
        }
        return (_ctx, _cache) => {
            const _component_v_select = _resolveComponent("v-select");
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_avatar = _resolveComponent("v-avatar");
            const _component_v_list_item_title = _resolveComponent("v-list-item-title");
            const _component_v_list_item_subtitle = _resolveComponent("v-list-item-subtitle");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_card_text = _resolveComponent("v-card-text");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_menu = _resolveComponent("v-menu");
            const _component_v_list_item = _resolveComponent("v-list-item");
            const _component_v_list = _resolveComponent("v-list");
            const _component_v_pagination = _resolveComponent("v-pagination");
            return (manifest.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, " Showing " + _toDisplayString(displayStart.value) + "-" + _toDisplayString(displayEnd.value) + " of " + _toDisplayString(numItems.value) + " items ", 1),
                        _createVNode(_component_v_select, {
                            modelValue: itemsPerPage.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((itemsPerPage).value = $event)),
                            items: perPageOptions,
                            density: "compact",
                            variant: "outlined",
                            "hide-details": "",
                            class: "items-per-page-select",
                            style: { "max-width": "120px" }
                        }, null, 8, ["modelValue"])
                    ]),
                    (manifest.value.items && numItems.value > 0)
                        ? (_openBlock(), _createBlock(_component_v_list, {
                            key: 0,
                            class: "manifest-list",
                            "bg-color": "transparent"
                        }, {
                            default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedItems.value, ([id, item]) => {
                                    return (_openBlock(), _createBlock(_component_v_list_item, {
                                        key: id,
                                        rounded: "lg",
                                        class: "manifest-item mb-1"
                                    }, {
                                        prepend: _withCtx(() => [
                                            _createVNode(_component_v_avatar, {
                                                size: "32",
                                                color: "grey-lighten-3",
                                                rounded: "lg",
                                                class: "mr-3"
                                            }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_icon, {
                                                        size: "16",
                                                        color: "grey-darken-1"
                                                    }, {
                                                        default: _withCtx(() => [
                                                            _createTextVNode(_toDisplayString(getFileIcon(item.relpath)), 1)
                                                        ]),
                                                        _: 2
                                                    }, 1024)
                                                ]),
                                                _: 2
                                            }, 1024)
                                        ]),
                                        append: _withCtx(() => [
                                            (_unref(store).hasSignedUrlPlugin)
                                                ? (_openBlock(), _createBlock(_component_v_btn, {
                                                    key: 0,
                                                    size: "small",
                                                    variant: "tonal",
                                                    color: "primary",
                                                    icon: "mdi-download",
                                                    loading: downloadingItems[id],
                                                    onClick: ($event) => (downloadItem(id, item.relpath))
                                                }, null, 8, ["loading", "onClick"]))
                                                : (_openBlock(), _createBlock(_component_v_menu, {
                                                    key: 1,
                                                    location: "start",
                                                    "close-on-content-click": false
                                                }, {
                                                    activator: _withCtx(({ props }) => [
                                                        _createVNode(_component_v_btn, _mergeProps({ ref_for: true }, props, {
                                                            size: "small",
                                                            variant: "tonal",
                                                            color: "primary",
                                                            icon: "mdi-download",
                                                            onClick: ($event) => (update_fetch_identifier(id))
                                                        }), null, 16, ["onClick"])
                                                    ]),
                                                    default: _withCtx(() => [
                                                        _createVNode(_component_v_card, {
                                                            "min-width": "440",
                                                            rounded: "lg"
                                                        }, {
                                                            default: _withCtx(() => [
                                                                _createVNode(_component_v_card_text, { class: "text-body-2 pb-2" }, {
                                                                    default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                                                                            _createTextVNode(" Fetch this item to get an absolute path on disk: ", -1)
                                                                        ]))]),
                                                                    _: 1
                                                                }),
                                                                _createVNode(_component_v_card_text, { class: "pt-0" }, {
                                                                    default: _withCtx(() => [
                                                                        _createVNode(_component_v_text_field, {
                                                                            "model-value": fetch_command.value,
                                                                            readonly: "",
                                                                            density: "compact",
                                                                            variant: "outlined",
                                                                            "hide-details": "",
                                                                            rounded: "lg",
                                                                            "bg-color": "grey-lighten-4"
                                                                        }, {
                                                                            "append-inner": _withCtx(() => [
                                                                                _createVNode(_component_v_btn, {
                                                                                    icon: "mdi-content-copy",
                                                                                    size: "small",
                                                                                    variant: "text",
                                                                                    onClick: _cache[1] || (_cache[1] = ($event) => (copyToClipboard(fetch_command.value)))
                                                                                })
                                                                            ]),
                                                                            _: 1
                                                                        }, 8, ["model-value"])
                                                                    ]),
                                                                    _: 1
                                                                })
                                                            ]),
                                                            _: 1
                                                        })
                                                    ]),
                                                    _: 2
                                                }, 1024))
                                        ]),
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_list_item_title, { class: "text-body-2 font-weight-medium" }, {
                                                default: _withCtx(() => [
                                                    _createTextVNode(_toDisplayString(item.relpath), 1)
                                                ]),
                                                _: 2
                                            }, 1024),
                                            _createVNode(_component_v_list_item_subtitle, { class: "text-caption text-medium-emphasis" }, {
                                                default: _withCtx(() => [
                                                    _createTextVNode(_toDisplayString(filesize(item.size_in_bytes)), 1)
                                                ]),
                                                _: 2
                                            }, 1024)
                                        ]),
                                        _: 2
                                    }, 1024));
                                }), 128))
                            ]),
                            _: 1
                        }))
                        : _createCommentVNode("", true),
                    (numItems.value === 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            _createVNode(_component_v_icon, {
                                size: "48",
                                color: "grey-lighten-1",
                                class: "mb-2"
                            }, {
                                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                        _createTextVNode("mdi-file-tree", -1)
                                    ]))]),
                                _: 1
                            }),
                            _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-body-2" }, "No items in this dataset", -1))
                        ]))
                        : _createCommentVNode("", true),
                    (totalPages.value > 1)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            _createVNode(_component_v_pagination, {
                                modelValue: currentPage.value,
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((currentPage).value = $event)),
                                length: totalPages.value,
                                "total-visible": 5,
                                density: "compact",
                                rounded: ""
                            }, null, 8, ["modelValue", "length"])
                        ]))
                        : _createCommentVNode("", true)
                ]))
                : _createCommentVNode("", true);
        };
    }
});
